import React, { useLayoutEffect, useState } from 'react';
import './JobStatistics.less';
import { JobType } from '../../../../redux/jobs/jobs.types';
import Toolbar from '../../../../components/envago/Toolbar/Toolbar';
import Card from '../../../../components/envago/Card/Card';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import ProgressBar from '../../../../components/envago/ProgressBar/ProgressBar';
import EnergyIcon from '../../../../components/envago/EnergyIcon/EnergyIcon';
import { METER_TYPES, resolveMeterColorTailwindClass } from '../../../../helper/MeterHelper';
import { countGroupBy, groupBy, objectToArray } from '../../../../helper/GroupHelper';
import { formatDate, toNow } from '../../../../helper/DateHelper';
import Information, { InformationType } from '../../../../components/envago/Information/Information';
import dayjs from 'dayjs';
import BasicScreen from '../../../common/BasicScreen/BasicScreen';
import { Trans, useTranslation } from 'react-i18next';
import Collapsable from '../../../../components/envago/Collapsable/Collapsable';
import { useDispatch } from 'react-redux';
import { push } from 'redux-first-history';
import JobListShortItem from '../JobListShortItem/JobListShortItem';
import { useFieldVisibility } from '../../../../fieldVisibility/useFieldVisibility';

interface StatisticType {
    done: number;
    byType: {
        [key: string]: {
            done: number;
            total: number;
        };
    };
    latestExecutionList: {
        key: string;
        values: {
            false?: number;
            true?: number;
        };
        jobs?: JobType[];
    }[];
}

const JobStatistics = ({ jobs }: { jobs: JobType[] }) => {
    const { t } = useTranslation();
    const [statistic, setStatistic] = useState<StatisticType | undefined>(undefined);
    const dispatch = useDispatch();
    // Einmal auf Listenebene und als Prop an jede Zeile: eine Zeile je Auftrag
    // baut sonst ein Store-Abo je Zeile (Konvention aus Issue #331).
    const visibility = useFieldVisibility();

    useLayoutEffect(() => {
        const doneCount = jobs.reduce((res, job) => (job.done ? res + 1 : res), 0);

        // groupBy calls the key mapper once per job. dayjs parsing plus format is
        // expensive at thousands of jobs while the distinct date count is tiny,
        // so memoise per raw value.
        const formattedDateCache = new Map<string, string>();
        const formatGroupDate = (raw: string) => {
            const cached = formattedDateCache.get(raw);
            if (cached !== undefined) return cached;
            const formatted = dayjs(raw).format('YYYY-MM-DD');
            formattedDateCache.set(raw, formatted);
            return formatted;
        };

        // Accumulate in place — spreading the accumulator per job allocated a new
        // object for every one of them.
        const byType: { [key: string]: { done: number; total: number } } = {};
        for (const job of jobs) {
            const key = job.jobInfo.meterType || '';
            const entry = byType[key] || (byType[key] = { done: 0, total: 0 });
            entry.total += 1;
            if (job.done) entry.done += 1;
        }

        setStatistic({
            latestExecutionList: objectToArray(groupBy(jobs, 'jobConstraints.latestExecution', formatGroupDate)).map((e) => ({
                key: e.key,
                values: countGroupBy(e.values as any[], 'done'),
                jobs: e.values as JobType[],
            })),
            done: doneCount,
            byType,
        });
    }, [jobs]);

    return (
        <BasicScreen>
            <Toolbar title={t('jobs.home.title')} />
            {(statistic?.latestExecutionList[0]?.values.false || 0) > 0 && dayjs(statistic?.latestExecutionList[0]?.key).diff(dayjs(), 'day') < 5 && (
                <Information type={InformationType.INFO} className={'mb-2'}>
                    <span>
                        <Trans
                            i18nKey={'jobs.home.expireWarning'}
                            values={{
                                expireDate: dayjs(statistic?.latestExecutionList[0]?.key).format('DD.MM.YYYY'),
                                expireCount: statistic?.latestExecutionList[0]?.values.false || 0,
                                count: statistic?.latestExecutionList[0]?.values.false || 0,
                            }}
                        >
                            <b>Achtung</b> am {dayjs(statistic?.latestExecutionList[0]?.key).format('DD.MM.YYYY')} laufen{` `}
                            {statistic?.latestExecutionList[0]?.values.false || 0} Aufträge ab, die du noch nicht abgelesen hast!
                        </Trans>
                        <br />
                    </span>
                </Information>
            )}

            <div className={'grid xl:grid-cols-2 gap-2'}>
                <Card aria={{ role: 'group', 'aria-label': 'Fortschritt' }}>
                    <h2>{t('jobs.home.statistic.title')}</h2>
                    <LabeledList>
                        <OptionalLabel label={t('jobs.home.statistic.total')}>
                            {jobs.length} {t('common.reading', { count: jobs.length })}
                        </OptionalLabel>
                        <OptionalLabel label={t('jobs.home.statistic.done')}>
                            <ProgressBar
                                showPercentage={true}
                                value={statistic?.done || 0}
                                total={jobs.length}
                                showNumbers={true}
                                progress={(statistic?.done || 0) / jobs.length}
                            />
                        </OptionalLabel>
                        <OptionalLabel>&nbsp;</OptionalLabel>

                            {Object.entries(statistic?.byType || {}).sort(([k1],[k2]) => k1.localeCompare(k2)).map(([k, v]) => {
                                return (

                                        <OptionalLabel
                                            key={`statistic-${k}`}
                                            className={'self-center'}
                                            label={
                                                <div className={'flex '}>
                                                    <EnergyIcon meterType={k as METER_TYPES} /> {t(`common.metertypes.${k}`)}
                                                </div>
                                            }
                                        >
                                            <ProgressBar
                                                progress={v.done / v.total}
                                                total={v.total}
                                                value={v.done}
                                                showNumbers={true}
                                                showPercentage={true}
                                                barClassName={'bg-gray-200'}
                                                activeBarClassName={`bg-${resolveMeterColorTailwindClass(k as METER_TYPES)}`}
                                            />
                                        </OptionalLabel>

                                );
                            })}

                    </LabeledList>
                </Card>
                <Card aria={{ role: 'group', 'aria-label': `Fortschritt ${t('jobs.home.period.title')}` }}>
                    <h2>{t('jobs.home.period.title')}</h2>

                    <p className={'description'}>{t('jobs.home.period.description')}</p>

                    <LabeledList>
                        <>
                            {statistic?.latestExecutionList.map((entry) => {
                                const done = entry.values.true || 0;
                                const total = done + (entry.values.false || 0);
                                const jobs = entry.jobs;

                                if (entry.key === 'default') return null;

                                return (
                                    <OptionalLabel
                                        key={`latestExecution-${entry.key}`}
                                        label={
                                            <div>
                                                {t('common.until')} {formatDate(entry.key)}
                                                <br />
                                                <small>{toNow(entry.key)}</small>
                                            </div>
                                        }
                                    >
                                        <ProgressBar progress={done / total} total={total} value={done} showNumbers={true} showPercentage={true} />
                                        {/* One entry per job in this period — mounting those eagerly
                                            put every job of the account into the DOM while collapsed. */}
                                        <Collapsable triggerText={t('jobs.home.period.showMeters')} className={'text-sm'} lazyChildren={true}>
                                            <div role={'list'}>
                                                {jobs?.map((job) => {
                                                    return <JobListShortItem key={job.id} job={job} visibility={visibility} onClick={() => dispatch(push(`/ablesung/${job.id}`))} />;
                                                })}
                                            </div>
                                        </Collapsable>
                                    </OptionalLabel>
                                );
                            })}
                        </>
                    </LabeledList>
                </Card>
            </div>
        </BasicScreen>
    );
};

export default JobStatistics;
